import { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import Table from '@aytm/app/stats/components/data-views/table/components/Table'

import style from '@aytm/app/stats/page/question-views/cjc/components/items/styles/Items.module.scss'

// One conjoint subquestion (attribute). Split into a clickable header and a body so the
// generic ItemsList owns the collapse chrome; this file only supplies conjoint content.

const identity = text => text

// Header: expand/collapse label, "<pos>. <text>", and the importance bar.
export const ItemHeader = ({ item, bAnswer, collapsed, processText }) => {
  const importance = bAnswer?.importance ?? 0

  return (
    <div className={style.description}>
      <span className={style.title}>
        <span className={style.titleIndex}>{item.position}</span>
        {processText(item.text)}
      </span>

      <div className={style.progressStatus}>
        <span className={style.progressText}>{importance.toFixed(1)}%</span>
      </div>

      <span className={classNames(style.expand, { [style.expandOpen]: !collapsed })}>
        <i className="icon-aytmfd-chevron-bracket" />
      </span>
    </div>
  )
}

ItemHeader.propTypes = {
  item: PropTypes.object.isRequired,
  bAnswer: PropTypes.object,
  collapsed: PropTypes.bool,
  processText: PropTypes.func
}

ItemHeader.defaultProps = { processText: identity }

// Build the answer-variant table props (Options | coefficient), rows sorted by
// coefficient desc; clicking an option picks it into the current package.
const buildItemTable = ({ id, item, bAnswer, processText, onPackageChange }) => {
  const answerVariants = item.answerVariants.slice().sort((a, b) => {
    const oa = bAnswer[`a${a.position}`]?.original
    const ob = bAnswer[`a${b.position}`]?.original
    return oa === ob ? a.position - b.position : ob - oa
  })

  const selectedIndex = answerVariants.findIndex(v => v.position === bAnswer.selected)

  return {
    id,
    cols: [
      { type: 'title', title: 'Options' },
      { type: 'value', title: bAnswer.importance_title, width: 182 }
    ],
    rows: answerVariants.map((av, avi) => {
      const a = bAnswer[`a${av.position}`] || {}
      const isSelected = bAnswer.selected === av.position

      const row = {
        position: av.position,
        cells: [
          {
            title: `${av.position}. ${processText(av.text)}`,
            onClick: () => onPackageChange(item.position, av.position)
          },
          { value: '%' === a.prefix && isSelected ? 'Selected' : `${a.value}${a.prefix}`, loading: false }
        ]
      }

      if (isSelected) {
        row.cells[0].classNames = ['bold']
        row.cells[1].classNames = ['bold']
      }
      // variants ranked below the selected one are worse trade-offs -> flagged
      if (selectedIndex < avi) {
        row.cells[1].classNames = ['error']
      }
      if (Object.hasOwn(av, 'image')) {
        row.cells[0].image = av.image
      }

      return row
    })
  }
}

// Body: the answer-variant coefficient table + package picker.
export const ItemBody = ({ id, item, bAnswer, processText, onPackageChange }) => {
  const tableProps = useMemo(
    () => buildItemTable({ id, item, bAnswer, processText, onPackageChange }),
    [id, item, bAnswer, processText, onPackageChange]
  )

  return (
    <div className={style.itemBody}>
      <Table {...tableProps} />
    </div>
  )
}

ItemBody.propTypes = {
  id: PropTypes.string,
  item: PropTypes.object.isRequired,
  bAnswer: PropTypes.object.isRequired,
  processText: PropTypes.func,
  onPackageChange: PropTypes.func.isRequired
}

ItemBody.defaultProps = { processText: identity }
